import {
    Apartment,
    Link as ChainLink,
    LocationOn,
    MonetizationOn,
    Work as WorkFilled,
} from '@mui/icons-material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
    Avatar,
    Box,
    Container,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material'
import { GetCompanyQuery } from '__generated__/graphql'
import React, { Dispatch, SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import CompanyStatusPill from 'recruiter/components/CompanyStatusPill'
import IndustryOrSectorList from 'recruiter/components/IndustryOrSectorList'
import { recruiterRouteConfig } from 'recruiter/routes'
import { CompanyStatus } from 'recruiter/types/CompanyStatus'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getPxAsRem } from 'shared/utils'
import {
    DesktopHeader,
    Header,
    InfoRow,
    StyledContainer,
    StyledHeader,
    WebLink,
} from './styled'

interface ProfileHeaderProps {
    data: GetCompanyQuery
    setEdit: Dispatch<SetStateAction<boolean>>
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ data, setEdit }) => {
    const isMobile = useIsMobile()
    const industries = data.company.industries ?? []
    const sectors = data.company.sectors ?? []
    const navigate = useNavigate()

    const companyInfo = (
        <Header>
            <Container
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: getPxAsRem(8),
                }}
            >
                <Avatar src={data?.company.profileImageKey ?? undefined}>
                    {data?.company.value[0]}
                </Avatar>
                <Typography
                    variant="h3"
                    sx={{ fontFamily: 'Roboto Serif', fontSize: '1.5rem' }}
                >
                    {data.company.value}
                </Typography>
            </Container>
            <CompanyStatusPill
                status={
                    data.company.status?.status || CompanyStatus['No Status']
                }
                nonPoachDate={data.company.nonPoachDate}
            />
        </Header>
    )

    const industryInfo = (
        <InfoRow
            title={`Industry: \n${industries.map((industry) => industry.industry.value + '\n').join('')}`}
        >
            <Apartment color="action" />
            <IndustryOrSectorList
                items={industries}
                limit={2}
                sx={{ display: 'flex', alignItems: 'center' }}
            />
        </InfoRow>
    )

    const sectorInfo = (
        <InfoRow
            title={`Sector: \n${sectors.map((sector) => sector.sector.value + '\n').join('')}`}
        >
            <WorkFilled color="action" />
            <IndustryOrSectorList
                items={sectors}
                limit={2}
                sx={{ display: 'flex', alignItems: 'center' }}
            />
        </InfoRow>
    )

    const urlInfo = (
        <InfoRow title="Website">
            <ChainLink color="action" />
            <WebLink
                href={
                    (data.company.url?.startsWith('http')
                        ? data.company.url
                        : `https://${data.company.url}`) ?? ''
                }
                title={data.company.url ?? undefined}
                target="_blank"
            >
                <Typography variant="body2">{data.company.url}</Typography>
            </WebLink>
        </InfoRow>
    )

    const locationInfo = (
        <InfoRow title="Office Location">
            <LocationOn color="action" />
            <Typography variant="body2">
                {data.company.headquarters?.officeLocation?.value ?? ''}
            </Typography>
        </InfoRow>
    )

    const fundSizeInfo = (
        <InfoRow title="Fund Size">
            <MonetizationOn color="action" />
            <Typography variant="body2">
                {data.company.companyFunds?.[0]
                    ? `${data.company.companyFunds?.[0].fundSizeMillions}M fund size in ${data.company.companyFunds?.[0].year}`
                    : ''}
            </Typography>
        </InfoRow>
    )

    const [open, setOpen] = React.useState(false)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
        setOpen(true)
    }
    const handleMenuClose = () => {
        setAnchorEl(null)
        setOpen(false)
    }

    const actionMenu = (
        <Menu
            id="action-menu"
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            MenuListProps={{
                'aria-labelledby': 'action-button',
            }}
            sx={{
                border: '1px solid',
                borderColor: 'other.lightBorder',
            }}
            onClick={() => setOpen(!open)}
            open={open}
            onClose={() => handleMenuClose}
            anchorEl={anchorEl}
        >
            <MenuItem onClick={() => setEdit(true)}>Edit Profile</MenuItem>
            {/*<MenuItem onClick={() => {}}>Update Status</MenuItem>*/}
            <MenuItem
                onClick={() => {
                    navigate(
                        `${recruiterRouteConfig.companies.path}/${data.company.id}/clientPermissions`
                    )
                }}
            >
                Access Permissions
            </MenuItem>
        </Menu>
    )

    if (isMobile) {
        return (
            <StyledHeader>
                <Container sx={{ justifyContent: 'center' }}>
                    {companyInfo}
                </Container>

                <Stack gap={getPxAsRem(16)} alignItems="center">
                    <Stack
                        direction="row"
                        spacing={4}
                        sx={{ paddingLeft: getPxAsRem(24) }}
                    >
                        {industryInfo}
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={4}
                        sx={{ paddingLeft: getPxAsRem(24) }}
                    >
                        {sectorInfo}
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={4}
                        sx={{ paddingLeft: getPxAsRem(24) }}
                    >
                        {locationInfo}
                        {fundSizeInfo}
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={4}
                        sx={{ paddingLeft: getPxAsRem(24) }}
                    >
                        {urlInfo}
                    </Stack>
                </Stack>
            </StyledHeader>
        )
    }

    // DESKTOP:
    return (
        <DesktopHeader disableGutters maxWidth={false}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: getPxAsRem(16, 24),
                }}
            >
                <Avatar
                    src={data?.company.profileImageKey ?? undefined}
                    sx={{
                        width: getPxAsRem(100),
                        height: getPxAsRem(100),
                        fontSize: '3rem',
                    }}
                >
                    {data?.company.value[0]}
                </Avatar>

                <Stack direction="column" spacing={1}>
                    <StyledContainer sx={{ gap: getPxAsRem(8) }}>
                        <Typography
                            variant="h2"
                            sx={{
                                fontFamily: 'Roboto Serif',
                                fontSize: '2.125rem',
                            }}
                        >
                            {data.company.value}
                        </Typography>
                        <CompanyStatusPill
                            status={
                                data.company.status?.status ||
                                CompanyStatus['No Status']
                            }
                            nonPoachDate={data.company.nonPoachDate}
                        />
                    </StyledContainer>
                    <Stack
                        direction="row"
                        spacing={4}
                        sx={{ paddingLeft: getPxAsRem(24) }}
                    >
                        {industryInfo}
                        {sectorInfo}
                        {locationInfo}
                        {fundSizeInfo}
                        {urlInfo}
                    </Stack>
                </Stack>
            </Box>
            <Box sx={{ width: 'auto', display: 'flex', alignItems: 'end' }}>
                <IconButton
                    id="action-button"
                    aria-controls={open ? 'action-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleMenuOpen}
                >
                    <MoreVertIcon />
                </IconButton>
            </Box>
            {actionMenu}
        </DesktopHeader>
    )
}

export default ProfileHeader
